import {Component, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {NgIf} from '@angular/common';
import {Router} from '@angular/router';
import {HttpEventType} from '@angular/common/http';

import {OtpService} from '../../api/otp.service';
import {BaseService} from '../../api/base.service';
import {SessionStorageService} from '../../core/services/session-storage.service';
import {ApiResponse} from '../../api/api-response.model';
import {Gender, InstituteType, LocationType} from '../../api/enum/enum.model';
import {storageKey} from '../../api/enum/storage.constants';
import {City, States} from '../../api/enum/city-state.model';

@Component({
  selector: 'app-institute-information',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, NgIf],
  templateUrl: './institute-information.component.html',
  styleUrl: './institute-information.component.scss'
})
export class InstituteInformationComponent implements OnInit {

  registerForm: FormGroup;

  genders: Gender[] = [];
  instituteTypes: InstituteType[] = [];
  locationType: LocationType[] = [];
  states: States[] = [];
  cities: City[] = [];

  emailOtpSent = false;
  mobileOtpSent = false;
  instituteEmailOtpSent = false;

  emailVerifying = false;
  mobileVerifying = false;
  instituteEmailVerifying = false;

  emailConfirming = false;
  mobileConfirming = false;
  instituteEmailConfirming = false;

  emailOtpError: string | null = null;
  mobileOtpError: string | null = null;
  instituteEmailOtpError: string | null = null;

  // ─── Logo Upload ───────────────────────────────────────────
  logoPreviewUrl: string | null = null;
  logoUploadUrl: string | null = null;
  logoUploading = false;
  logoUploadProgress = 0;
  logoUploadError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private storage: SessionStorageService,
    private otpService: OtpService,
    private baseService: BaseService,
    private router: Router
  ) {
    this.registerForm = this.fb.group({
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      dob: [''],
      gender: [''],
      email: ['', [Validators.required, Validators.email]],
      emailOtp: [''],
      emailVerified: [false],
      mobile: ['', Validators.required],
      mobileOtp: [''],
      mobileVerified: [false],
      name: ['', Validators.required],
      instituteEmail: ['', Validators.email],
      instituteEmailOtp: [''],
      instituteEmailVerified: [false],
      instituteType: ['', Validators.required],
      description: [''],
      website: [''],
      accreditation: [''],
      location: this.fb.group({
        locationTypeId: ['', Validators.required],
        addressLine1: ['', Validators.required],
        addressLine2: [''],
        stateId: ['', Validators.required],
        cityId: [{value: '', disabled: true}, Validators.required],
        postal_code: ['', Validators.required]
      })
    });
  }

  ngOnInit(): void {
    this.loadEnumData();
    this.loadInstituteEnumData();
    this.loadCityStateEnumData();
    this.loadLocationTypeEnumData();
    this.restoreForm();

    this.registerForm.get('location.stateId')?.valueChanges.subscribe(
      stateId => this.onStateChange(stateId)
    );
  }

  // ─── OTP — Email ───────────────────────────────────────────
  sendEmailOtp(): void {
    const email = this.registerForm.get('email')?.value;
    if (!email) return;
    this.emailOtpError = null;
    this.emailVerifying = true;
    this.otpService.sendOtp({otp_type: 'sign_up_otp', email}).subscribe({
      next: () => {
        this.emailOtpSent = true;
        this.emailVerifying = false;
      },
      error: (err) => {
        this.emailVerifying = false;
        this.emailOtpError = err?.error?.message?.message ?? 'Failed to send OTP. Please try again.';
      }
    });
  }

  verifyEmailOtp(): void {
    const email = this.registerForm.get('email')?.value;
    const emailOtp = this.registerForm.get('emailOtp')?.value;
    if (!email) return;
    this.emailOtpError = null;
    this.emailConfirming = true;
    this.otpService.verifyOtp({otp_type: 'sign_up_otp', email, received_otp: parseInt(emailOtp)}).subscribe({
      next: () => {
        this.emailConfirming = false;
        this.registerForm.patchValue({emailVerified: true});
        this.registerForm.get('email')?.disable();
        this.emailOtpSent = false;
      },
      error: (err) => {
        this.emailConfirming = false;
        this.emailOtpError = err?.error?.message?.message ?? 'OTP verification failed. Please try again.';
      }
    });
  }

  // ─── OTP — Mobile ──────────────────────────────────────────
  sendMobileOtp(): void {
    const mobile = this.registerForm.get('mobile')?.value;
    if (!mobile) return;
    this.mobileOtpError = null;
    this.mobileVerifying = true;
    this.otpService.sendOtp({otp_type: 'sign_up_otp', mobile: Number(mobile)}).subscribe({
      next: () => {
        this.mobileOtpSent = true;
        this.mobileVerifying = false;
      },
      error: (err) => {
        this.mobileVerifying = false;
        this.mobileOtpError = err?.error?.message?.message ?? 'Failed to send OTP. Please try again.';
      }
    });
  }

  verifyMobileOtp(): void {
    const mobile = this.registerForm.get('mobile')?.value;
    const mobileOtp = this.registerForm.get('mobileOtp')?.value;
    if (!mobile) return;
    this.mobileOtpError = null;
    this.mobileConfirming = true;
    this.otpService.verifyOtp({otp_type: 'sign_up_otp', mobile, received_otp: parseInt(mobileOtp)}).subscribe({
      next: () => {
        this.mobileConfirming = false;
        this.registerForm.patchValue({mobileVerified: true});
        this.registerForm.get('mobile')?.disable();
        this.mobileOtpSent = false;
      },
      error: (err) => {
        this.mobileConfirming = false;
        this.mobileOtpError = err?.error?.message?.message ?? 'OTP verification failed. Please try again.';
      }
    });
  }

  // ─── OTP — Institute Email ─────────────────────────────────
  sendInstituteEmailOtp(): void {
    const email = this.registerForm.get('instituteEmail')?.value;
    if (!email) return;
    this.instituteEmailOtpError = null;
    this.instituteEmailVerifying = true;
    this.otpService.sendOtp({otp_type: 'verify_institute_otp', email}).subscribe({
      next: () => {
        this.instituteEmailOtpSent = true;
        this.instituteEmailVerifying = false;
      },
      error: (err) => {
        this.instituteEmailVerifying = false;
        this.instituteEmailOtpError = err?.error?.message?.message ?? 'Failed to send OTP. Please try again.';
      }
    });
  }

  verifyInstituteEmailOtp(): void {
    const email = this.registerForm.get('instituteEmail')?.value;
    const emailOtp = this.registerForm.get('instituteEmailOtp')?.value;
    if (!email) return;
    this.instituteEmailOtpError = null;
    this.instituteEmailConfirming = true;
    this.otpService.verifyOtp({otp_type: 'verify_institute_otp', email, received_otp: parseInt(emailOtp)}).subscribe({
      next: () => {
        this.instituteEmailConfirming = false;
        this.registerForm.patchValue({instituteEmailVerified: true});
        this.registerForm.get('instituteEmail')?.disable();
        this.instituteEmailOtpSent = false;
      },
      error: (err) => {
        this.instituteEmailConfirming = false;
        this.instituteEmailOtpError = err?.error?.message?.message ?? 'OTP verification failed. Please try again.';
      }
    });
  }

  // ─── Change verified contact ───────────────────────────────
  changeEmail(): void {
    this.registerForm.patchValue({emailVerified: false, emailOtp: ''});
    this.registerForm.get('email')?.enable();
    this.emailOtpSent = false;
    this.emailOtpError = null;
  }

  changeMobile(): void {
    this.registerForm.patchValue({mobileVerified: false, mobileOtp: ''});
    this.registerForm.get('mobile')?.enable();
    this.mobileOtpSent = false;
    this.mobileOtpError = null;
  }

  changeInstituteEmail(): void {
    this.registerForm.patchValue({instituteEmailVerified: false, instituteEmailOtp: ''});
    this.registerForm.get('instituteEmail')?.enable();
    this.instituteEmailOtpSent = false;
    this.instituteEmailOtpError = null;
  }

  // ─── State / City ──────────────────────────────────────────
  onStateChange(stateId: number): void {
    const selectedState = this.states.find(s => s.id === +stateId);
    this.cities = selectedState?.cities ?? [];
    this.registerForm.patchValue({location: {cityId: null}});
    const cityControl = this.registerForm.get('location.cityId');
    this.cities.length ? cityControl?.enable() : cityControl?.disable();
  }

  // ─── Logo Upload ───────────────────────────────────────────
  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.logoUploadError = 'Invalid file type. Please upload an image (JPG, PNG, etc.).';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.logoUploadError = 'File exceeds the 2 MB limit. Please choose a smaller image.';
      return;
    }

    this.logoUploadError = null;
    const reader = new FileReader();
    reader.onload = () => {
      this.logoPreviewUrl = reader.result as string;
    };
    reader.readAsDataURL(file);
    this.uploadLogo(file);
  }

  private uploadLogo(file: File): void {
    this.logoUploading = true;
    this.logoUploadProgress = 0;
    this.logoUploadUrl = null;

    this.baseService.uploadFile(file).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.logoUploadProgress = Math.round(100 * event.loaded / event.total);
        } else if (event.type === HttpEventType.Response) {
          const body = event.body as { data: { url: string } };
          this.logoUploadUrl = body?.data?.url ?? null;
          this.logoUploading = false;
          this.logoUploadProgress = 100;
        }
      },
      error: () => {
        this.logoUploading = false;
        this.logoUploadError = 'Upload failed. Please try again.';
        this.logoPreviewUrl = null;
      }
    });
  }

  removeLogo(): void {
    this.logoPreviewUrl = null;
    this.logoUploadUrl = null;
    this.logoUploading = false;
    this.logoUploadProgress = 0;
    this.logoUploadError = null;
  }

  // ─── Submit ────────────────────────────────────────────────
  submit(): void {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }
    const payload = this.registerForm.getRawValue();
    const {
      emailVerified, emailOtp, mobileVerified, mobileOtp,
      instituteEmailVerified, instituteEmailOtp, ...cleanPayload
    } = payload;

    const selectedGender = this.genders.find(g => g.value === +cleanPayload.gender);
    const selectedInstituteType = this.instituteTypes.find(t => t.value === +cleanPayload.instituteType);
    const selectedLocationType = this.locationType.find(lt => lt.value === +cleanPayload.location.locationTypeId);
    const selectedState = this.states.find(s => s.id === +cleanPayload.location.stateId);
    const selectedCity = selectedState?.cities.find(c => c.id === +cleanPayload.location.cityId);

    const resolvedData = {
      rawForm: payload,
      admin: {
        firstName: cleanPayload.firstName,
        middleName: cleanPayload.middleName,
        lastName: cleanPayload.lastName,
        dob: cleanPayload.dob,
        genderId: cleanPayload.gender,
        gender: selectedGender?.label ?? '',
        email: cleanPayload.email,
        mobile: cleanPayload.mobile,
      },
      institute: {
        name: cleanPayload.name,
        email: cleanPayload.instituteEmail,
        website: cleanPayload.website,
        accreditation: cleanPayload.accreditation,
        description: cleanPayload.description || null,
        logo: this.logoUploadUrl || null,
        instituteType: {
          instituteTypeId: cleanPayload.instituteType,
          instituteTypeName: selectedInstituteType?.label ?? '',
        },
        location: {
          locationType: {
            locationTypeId: cleanPayload.location.locationTypeId,
            locationTypeName: selectedLocationType?.label ?? '',
          },
          addressLine1: cleanPayload.location.addressLine1,
          addressLine2: cleanPayload.location.addressLine2,
          state: {stateId: cleanPayload.location.stateId, stateName: selectedState?.name ?? ''},
          city: {cityId: cleanPayload.location.cityId, cityName: selectedCity?.name ?? ''},
          postal_code: cleanPayload.location.postal_code,
        },
      },
    };

    this.storage.set(storageKey.instituteFormData, resolvedData);
    this.router.navigateByUrl('/subscription');
  }

  // ─── Restore saved form ────────────────────────────────────
  private restoreForm(): void {
    const saved = this.storage.get<any>(storageKey.instituteFormData);
    if (!saved?.rawForm) return;

    const raw = saved.rawForm;

    // Restore cities for the saved state before patching
    if (raw.location?.stateId) {
      const selectedState = this.states.find(s => s.id === +raw.location.stateId);
      this.cities = selectedState?.cities ?? [];
      if (this.cities.length) {
        this.registerForm.get('location.cityId')?.enable();
      }
    }

    this.registerForm.patchValue(raw);

    // Re-disable verified fields
    if (raw.emailVerified) {
      this.registerForm.get('email')?.disable();
    }
    if (raw.mobileVerified) {
      this.registerForm.get('mobile')?.disable();
    }
    if (raw.instituteEmailVerified) {
      this.registerForm.get('instituteEmail')?.disable();
    }
  }

  // ─── Private loaders ───────────────────────────────────────
  private loadEnumData(): void {
    const response = this.storage.get<ApiResponse<{ gender_type: Gender[] }>>(storageKey.enumData);
    if (!response?.data) {
      this.router.navigateByUrl('/');
      return;
    }
    this.genders = response.data.gender_type;
  }

  private loadInstituteEnumData(): void {
    const response = this.storage.get<ApiResponse<{ institution_type: InstituteType[] }>>(storageKey.instituteEnumData);
    if (!response?.data) {
      this.router.navigateByUrl('/');
      return;
    }
    this.instituteTypes = response.data.institution_type;
  }

  private loadLocationTypeEnumData(): void {
    const response = this.storage.get<ApiResponse<{ location_type: LocationType[] }>>(storageKey.instituteEnumData);
    if (!response?.data) {
      this.router.navigateByUrl('/');
      return;
    }
    this.locationType = response.data.location_type;
  }

  private loadCityStateEnumData(): void {
    const response = this.storage.get<ApiResponse<States[]>>(storageKey.cityState);
    if (!response?.data) {
      this.router.navigateByUrl('/');
      return;
    }
    this.states = response.data;
  }
}
